import {
    CapacityValue,
    CountType,
    FlightId,
    FlightModelCounts,
    Otmv,
    TimePeriod,
    TimeRange,
    TimeSliceStandardSubCounts,
    TimeSliceSubCounts,
} from 'app/model/CORE';
import React, { MouseEvent, PureComponent, ReactNode } from 'react';
import { Range, Scale, TimeScale } from 'app/common/graph/Scale';
import TrafficCountUtils from 'app/common/util/TrafficCountUtils';
import TimeRangeUtils from 'app/common/util/TimeRangeUtils';
import TimeUtils from 'app/common/util/TimeUtils';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import { SvgUtils } from 'app/common/util/SvgUtils';
import GraphUtils from 'app/common/graph/GraphUtils';
import { AxisOrientation, CountAxis, TimeAxis, TimeLabelFormat } from 'app/common/graph/Axis';
import AutoSizer from 'react-virtualized-auto-sizer';
import './CountsGraph.less';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import memoizeOne from 'memoize-one';
import { RegulationPeriods } from 'app/model/client/CORE';
import Utils from 'app/common/util/Utils';
import AppContext from 'app/common/context/AppContext';

const VERTICAL_AXIS_WIDTH = 30;
const HORZ_AXIS_HEIGHT = 30;
const MARGIN_TOP = 5;
const COUNTS_RANGE_MARGIN = 5;
const STANDARD_SUB_COUNTS = [
    'atcActivated',
    'tactActivatedWithoutFsa',
    'ifpl',
    'pfd',
    'tactActivatedWithFsa',
    'suspended',
];
//const CDM_SUB_COUNTS = ["atcActivated", "cdmOffblock", "cdmPresequenced", "tactActivated", "ifpl", "pfdConfirmed", "pfd", "suspended"];
const REGULATION_BAR_HEIGHT = 3;

function getComparisonCountsLine(key: string, x1: number, y1: number, x2: number, y2: number) {
    return (
        <line
            key={key}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="black"
            strokeWidth={'1px'}
            shapeRendering="geometricPrecision"
        />
    );
}

function getRegulationLine(key: string, x1: number, y1: number, x2: number, y2: number, isProposal?: boolean) {
    return (
        <line
            key={`${isProposal ? 'proposal-' : ''}${key}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="white"
            strokeWidth={'2px'}
            strokeDasharray={isProposal ? '0.5em 0.5em' : ''}
            shapeRendering="geometricPrecision"
        />
    );
}

interface CountsGraphProps {
    currentTime: Date;
    counts: FlightModelCounts;
    comparisonCounts?: FlightModelCounts;
    activePeriods: Array<TimePeriod>;
    timeRange: TimeRange;
    hourlyEntryCapacity?: Array<CapacityValue>;
    predictedCapacity?: Array<CapacityValue>;
    otmvDefns?: Array<Otmv>;
    regulationPeriods?: Array<RegulationPeriods>;
    proposalRegulationPeriods?: Array<RegulationPeriods>;
    hotspotsPeriods?: Array<TimePeriod>;
    highlightedFlightsType?: CountType; // Specifies whether the highlighted flight occupancies should consider entry time or full occupancy period - Default behaviour is OCCUPANCY
    highlightedFlightsOccupancy?: Map<FlightId, TimePeriod>;
    timeOver?: Date;
    onTimeOverChange?: (date: Date) => void;
    selectedPeriod?: TimePeriod;
    onSelectedPeriodChange?: (selectedPeriod: TimePeriod, bitMask?: number) => void;
    displayPeriod?: TimePeriod;
    hideCountAxis?: boolean;
    hideTimeAxis?: boolean;
    regOutlook?: number[];
}

interface CountsGraphState {
    timeOver: Date;
    xAxisTickInterval: number;
    yAxisTickInterval: number;
}

export class CountsGraph extends PureComponent<CountsGraphProps, CountsGraphState> {
    private svgRef = React.createRef<SVGSVGElement>();
    private timeScale: TimeScale;
    private countScale: Scale;
    private mouseDown: boolean;
    private selectedPeriod: TimePeriod;
    // Default scale for % from 0 to 100, the source scale will be updated by the AutoSizer
    private regOutlookScale: Scale = new Scale(new Range(100, 0), new Range(0, 100));

    state = {
        timeOver: undefined,
        xAxisTickInterval: 30 * 60,
        yAxisTickInterval: 10,
    };

    componentDidMount(): void {
        this.updateScales();
    }

    componentDidUpdate(
        prevProps: Readonly<CountsGraphProps>,
        prevState: Readonly<CountsGraphState>,
        snapshot?: any,
    ): void {
        // Don't update timeRange if currentTime changes (keep initial one)
        if (prevProps !== this.props && prevProps.timeRange !== this.props.timeRange) {
            this.updateScales();
        }
    }

    getInactivePeriods = memoizeOne((timeRange: TimeRange, activePeriods: Array<TimePeriod>) => {
        return (
            activePeriods &&
            timeRange &&
            MonitoredLoadUtils.getInactivePeriods(TimeRangeUtils.getTimePeriod(timeRange), activePeriods)
        );
    });
    updateScales = () => {
        // Needs to be at least 1s, otherwise timescale will generate errors (division by 0)
        const defaultTimeRange = { from: this.props.currentTime, to: TimeUtils.addSeconds(this.props.currentTime, 1) };

        this.timeScale = TimeScale.newTimeScale(
            new Range(0, 100),
            this.props.displayPeriod || this.props.timeRange || defaultTimeRange,
            null,
            30,
        );
        this.countScale = new Scale(new Range(100, 0), this.getCountTargetRange());
        this.setState({
            xAxisTickInterval: TimeAxis.chooseTickInterval(
                this.timeScale,
                15,
                TimeLabelFormat.HH_MM,
                AxisOrientation.BOTTOM,
            ),
            yAxisTickInterval: CountAxis.chooseTickInterval(this.countScale, 11, AxisOrientation.LEFT),
        });
    };
    getTimeScale = () => {
        return this.timeScale;
    };
    getDisplayPeriod = (): TimePeriod => {
        const range = this.getTimeScale().getScale().target;
        return range && { from: this.getTimeScale().toDate(range.from), to: this.getTimeScale().toDate(range.to) };
    };
    getCountScale = () => {
        return this.countScale;
    };
    getTimeOver = () => {
        // Either return value from props (controller) or from state (uncontrolled)
        return this.props.timeOver || this.state.timeOver;
    };
    renderRegulationBackground = (regulationPeriods: Array<RegulationPeriods>) => {
        if (!regulationPeriods) return null;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();

        return regulationPeriods.map((subPeriods, idx) => {
            let applicability = TimePeriodUtils.coversAll(
                subPeriods.constraints.map(constraint => constraint.timePeriod),
            );
            if (!applicability?.from || !applicability?.to) return;

            // filter out elements outside of display period
            if (!TimePeriodUtils.intersects(this.getDisplayPeriod(), applicability)) return;
            applicability = TimePeriodUtils.intersection(this.getDisplayPeriod(), applicability);

            return (
                <rect
                    key={`"sub-period-${idx}"`}
                    fill="#4C587A"
                    x={tscale.toSource(applicability.from)}
                    y={cscale.source.to > cscale.source.from ? cscale.source.from : cscale.source.to}
                    width={tscale.toSource(applicability.to) - tscale.toSource(applicability.from)}
                    height={
                        cscale.source.to > cscale.source.from
                            ? cscale.source.to - cscale.source.from
                            : cscale.source.from - cscale.source.to
                    }
                    shapeRendering="geometricPrecision"
                />
            );
        });
    };
    renderInactivePeriods = () => {
        const inactivePeriods = this.getInactivePeriods(this.props.timeRange, this.props.activePeriods);
        if (!inactivePeriods) return null;

        const tscale = this.getTimeScale();
        const displayPeriod = this.getDisplayPeriod();
        const cscale = this.getCountScale();

        return inactivePeriods
            .filter(inactivePeriod => TimePeriodUtils.intersects(displayPeriod, inactivePeriod))
            .map((subPeriod, idx) => {
                if (!subPeriod.from || !subPeriod.to) return;

                const inactivePeriod = TimePeriodUtils.intersection(displayPeriod, subPeriod);
                return (
                    <rect
                        key={`"inactive-period-${idx}"`}
                        fill="#00000040"
                        x={tscale.toSource(inactivePeriod.from)}
                        y={cscale.source.to > cscale.source.from ? cscale.source.from : cscale.source.to}
                        width={tscale.toSource(inactivePeriod.to) - tscale.toSource(inactivePeriod.from)}
                        height={
                            cscale.source.to > cscale.source.from
                                ? cscale.source.to - cscale.source.from
                                : cscale.source.from - cscale.source.to
                        }
                        shapeRendering="geometricPrecision"
                    />
                );
            });
    };
    renderBackground = () => {
        const tscale = this.getTimeScale().getScale();
        const cscale = this.getCountScale();

        const fill = this.props.counts ? '#3C3C3C' : 'url(#normal-hashed)';
        return (
            <>
                <rect
                    fill={fill}
                    x={tscale.source.to > tscale.source.from ? tscale.source.from : tscale.source.to}
                    y={cscale.source.to > cscale.source.from ? cscale.source.from : cscale.source.to}
                    width={
                        tscale.source.to > tscale.source.from
                            ? tscale.source.to - tscale.source.from
                            : tscale.source.from - tscale.source.to
                    }
                    height={
                        cscale.source.to > cscale.source.from
                            ? cscale.source.to - cscale.source.from
                            : cscale.source.from - cscale.source.to
                    }
                    shapeRendering="geometricPrecision"
                />
                {this.renderRegulationBackground(this.props.regulationPeriods)}
                {this.renderRegulationBackground(this.props.proposalRegulationPeriods)}
            </>
        );
    };

    renderHorizontalGrid = interval => {
        const tscale = this.getTimeScale().getScale();
        const cscale = this.getCountScale();
        const range = cscale.target;
        let value = range.getFirstValue(interval);
        const increment = range.from <= range.to ? interval : -interval;
        let path = '';
        while (range.contains(value)) {
            const sourceVal = cscale.toSource(value);
            if (cscale.source.contains(sourceVal)) {
                path += ' M' + tscale.source.from + ',' + sourceVal + ' L' + tscale.source.to + ',' + sourceVal;
            }
            value += increment;
        }
        return <path d={path} stroke="#26262680" shapeRendering="crispEdges" />;
    };
    renderTimeSliceSubCounts = (slice: TimeSliceSubCounts, timePeriod: TimePeriod) => {
        if (!slice) return undefined;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();
        if (TrafficCountUtils.isStandardSubCounts(slice)) {
            const stdSubCounts: TimeSliceStandardSubCounts = slice;
            let cumulatedNbFlights = 0;
            return STANDARD_SUB_COUNTS.map(key => {
                const nbFlights = stdSubCounts[key];
                if (nbFlights) {
                    const prevCumulatedNbFlights = cumulatedNbFlights;
                    cumulatedNbFlights += nbFlights;

                    return (
                        <rect
                            key={`sub-count-${key}`}
                            className={`sub-count-${key}`}
                            x={tscale.toSource(timePeriod.from)}
                            y={cscale.toSource(cumulatedNbFlights)}
                            width={tscale.toSource(timePeriod.to) - tscale.toSource(timePeriod.from)}
                            height={cscale.toSource(prevCumulatedNbFlights) - cscale.toSource(cumulatedNbFlights)}
                            shapeRendering="crispEdges"
                        />
                    );
                }
            });
        } else if (TrafficCountUtils.isStandardRegulationSubCounts(slice)) {
            {
                /* CDM sub counts are not supported in this panel (no explicit needs expressed)*/
            }
        }
    };
    renderTimeSliceTotalCounts = (total: number, timePeriod: TimePeriod) => {
        if (!total) return undefined;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();

        return (
            <rect
                key={`sub-count-total`}
                className={`sub-count-${STANDARD_SUB_COUNTS[0]}`}
                x={tscale.toSource(timePeriod.from)}
                y={cscale.toSource(total)}
                width={tscale.toSource(timePeriod.to) - tscale.toSource(timePeriod.from)}
                height={cscale.toSource(0) - cscale.toSource(total)}
                shapeRendering="crispEdges"
            />
        );
    };
    renderCounts = () => {
        if (!this.props.counts) return undefined;

        const displayPeriod = this.getDisplayPeriod();
        return this.props.counts.counts.map((slice, index) => {
            const slicePeriod = TimeRangeUtils.getTimePeriodAtStep(this.props.timeRange, index);
            if (!TimePeriodUtils.intersects(displayPeriod, slicePeriod)) return null;

            const timePeriod = TimePeriodUtils.intersection(displayPeriod, slicePeriod);
            return (
                <g key={`slice-${index}`}>
                    {slice.subtotals && this.renderTimeSliceSubCounts(slice.subtotals, timePeriod)}
                    {!slice.subtotals && this.renderTimeSliceTotalCounts(slice.total, timePeriod)}
                </g>
            );
        });
    };
    renderComparisonCounts = () => {
        if (!this.props.comparisonCounts) return undefined;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();

        const graph = new Array<ReactNode>();
        let previousTotal = 0;
        this.props.comparisonCounts.counts?.forEach((slice, index) => {
            const timePeriod = TimeRangeUtils.getTimePeriodAtStep(this.props.timeRange, index);
            graph.push([
                getComparisonCountsLine(
                    `comparison-counts-slice#${index}-transition`,
                    tscale.toSource(timePeriod.from),
                    cscale.toSource(previousTotal),
                    tscale.toSource(timePeriod.from),
                    cscale.toSource(slice.total),
                ),
                getComparisonCountsLine(
                    `comparison-counts-slice#${index}-value`,
                    tscale.toSource(timePeriod.from),
                    cscale.toSource(slice.total),
                    tscale.toSource(timePeriod.to),
                    cscale.toSource(slice.total),
                ),
            ]);
            if (index === this.props.comparisonCounts.counts.length - 1) {
                graph.push(
                    getComparisonCountsLine(
                        `comparison-counts-slice#${index}-end`,
                        tscale.toSource(timePeriod.to),
                        cscale.toSource(slice.total),
                        tscale.toSource(timePeriod.to),
                        cscale.toSource(0),
                    ),
                );
            }
            previousTotal = slice.total;
        });
        return graph;
    };
    renderCapacity = (capacity: CapacityValue[], color: string) => {
        if (!capacity) return undefined;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();
        const displayPeriod = this.getDisplayPeriod();

        return capacity
            .filter(capacityValue => TimePeriodUtils.intersects(displayPeriod, capacityValue.timePeriod))
            .map((capacityValue, index) => {
                const timePeriod = TimePeriodUtils.intersection(displayPeriod, capacityValue.timePeriod);
                return (
                    <line
                        key={'hourly-entry-capacity-' + index}
                        x1={tscale.toSource(timePeriod.from)}
                        y1={cscale.toSource(capacityValue.hourlyCapacity)}
                        x2={tscale.toSource(timePeriod.to)}
                        y2={cscale.toSource(capacityValue.hourlyCapacity)}
                        stroke={color}
                        strokeWidth={'1px'}
                        shapeRendering="geometricPrecision"
                    />
                );
            });
    };
    renderOtmv = () => {
        if (!this.props.otmvDefns) return undefined;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();
        const displayPeriod = this.getDisplayPeriod();

        return this.props.otmvDefns
            .filter(otmv => TimePeriodUtils.intersects(displayPeriod, otmv.timePeriod))
            .map((otmv, index) => {
                const timePeriod = TimePeriodUtils.intersection(displayPeriod, otmv.timePeriod);
                return [
                    <line
                        key={'otmv-sustained-capacity-' + index}
                        x1={tscale.toSource(otmv.timePeriod.from)}
                        y1={cscale.toSource(otmv.sustainedValue)}
                        x2={tscale.toSource(otmv.timePeriod.to)}
                        y2={cscale.toSource(otmv.sustainedValue)}
                        stroke="orange"
                        strokeWidth={'1px'}
                        shapeRendering="geometricPrecision"
                    />,
                    <line
                        key={'otmv-peak-capacity-' + index}
                        x1={tscale.toSource(otmv.timePeriod.from)}
                        y1={cscale.toSource(otmv.peakValue)}
                        x2={tscale.toSource(otmv.timePeriod.to)}
                        y2={cscale.toSource(otmv.peakValue)}
                        stroke="red"
                        strokeWidth={'1px'}
                        shapeRendering="geometricPrecision"
                    />,
                ];
            });
    };
    renderRegulationPeriods = (regulationPeriods: RegulationPeriods[], isProposal: boolean) => {
        return regulationPeriods?.map(regPeriod => {
            if (regPeriod.calculationType === CountType.ENTRY && this.props.timeRange?.countDurationInMinutes === 60) {
                return this.renderRegulationPeriodsForHourlyCounts(regPeriod, isProposal);
            }
            if (
                regPeriod.calculationType === CountType.OCCUPANCY &&
                this.props.timeRange?.countDurationInMinutes * TimeUtils.MIN_TO_MS === regPeriod.occupancyDuration
            ) {
                return this.renderRegulationPeriodsForOccupancyCounts(regPeriod, isProposal);
            }
            return this.renderRegulationPeriodsForOtherCounts(regPeriod);
        });
    };

    renderRegulationPeriodsForHourlyCounts = (entryRegPeriods: RegulationPeriods, isProposal?: boolean) => {
        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();

        const graph = new Array<ReactNode>();
        let previousRate = 0;

        // Regulation sub-periods need to be ordered chronologically so that the display algorithm works fine
        const subPeriods = entryRegPeriods.constraints
            .slice()
            .sort((constraint1, constraint2) =>
                TimePeriodUtils.COMPARE_FUNCTION(constraint1.timePeriod, constraint2.timePeriod),
            );

        for (let idx = 0; idx < subPeriods.length; idx++) {
            const constraint = subPeriods[idx];

            if (constraint.timePeriod?.from && constraint.timePeriod?.to) {
                const rate = constraint.rate;
                graph.push([
                    getRegulationLine(
                        `sub-period-transition-${idx}`,
                        tscale.toSource(constraint.timePeriod.from),
                        cscale.toSource(previousRate),
                        tscale.toSource(constraint.timePeriod.from),
                        cscale.toSource(rate),
                        isProposal,
                    ),
                    getRegulationLine(
                        `sub-period-value-${idx}`,
                        tscale.toSource(constraint.timePeriod.from),
                        cscale.toSource(rate),
                        tscale.toSource(constraint.timePeriod.to),
                        cscale.toSource(rate),
                        isProposal,
                    ),
                ]);
                if (idx === subPeriods.length - 1) {
                    graph.push(
                        getRegulationLine(
                            `sub-period-transition-end`,
                            tscale.toSource(constraint.timePeriod.to),
                            cscale.toSource(rate),
                            tscale.toSource(constraint.timePeriod.to),
                            cscale.toSource(0),
                            isProposal,
                        ),
                    );
                }
                previousRate = rate;
            }
        }
        return graph;
    };
    renderRegulationPeriodsForOccupancyCounts = (occRegPeriods: RegulationPeriods, isProposal?: boolean) => {
        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();

        const graph = new Array<ReactNode>();
        let previousRate = 0;
        let previousPeakRate = 0;

        // Regulation sub-periods need to be ordered chronologically so that the display algorithm works fine
        const subPeriods = occRegPeriods.constraints
            .slice()
            .sort((constraint1, constraint2) =>
                TimePeriodUtils.COMPARE_FUNCTION(constraint1.timePeriod, constraint2.timePeriod),
            );

        for (let idx = 0; idx < subPeriods.length; idx++) {
            const constraint = subPeriods[idx];

            if (constraint.timePeriod?.from && constraint.timePeriod?.to) {
                const rate = constraint.rate;
                const peakRate = constraint.peakRate;
                graph.push([
                    getRegulationLine(
                        `occ-sub-period-transition-${idx}`,
                        tscale.toSource(constraint.timePeriod.from),
                        cscale.toSource(previousRate),
                        tscale.toSource(constraint.timePeriod.from),
                        cscale.toSource(rate),
                        isProposal,
                    ),
                    getRegulationLine(
                        `occ-sub-period-value-${idx}`,
                        tscale.toSource(constraint.timePeriod.from),
                        cscale.toSource(rate),
                        tscale.toSource(constraint.timePeriod.to),
                        cscale.toSource(rate),
                        isProposal,
                    ),
                    getRegulationLine(
                        `occ-peak-sub-period-transition-${idx}`,
                        tscale.toSource(constraint.timePeriod.from),
                        cscale.toSource(previousPeakRate),
                        tscale.toSource(constraint.timePeriod.from),
                        cscale.toSource(peakRate),
                        isProposal,
                    ),
                    getRegulationLine(
                        `occ-peak-sub-period-value-${idx}`,
                        tscale.toSource(constraint.timePeriod.from),
                        cscale.toSource(peakRate),
                        tscale.toSource(constraint.timePeriod.to),
                        cscale.toSource(peakRate),
                        isProposal,
                    ),
                ]);
                if (idx === subPeriods.length - 1) {
                    graph.push(
                        getRegulationLine(
                            `sub-period-transition-end`,
                            tscale.toSource(constraint.timePeriod.to),
                            cscale.toSource(rate),
                            tscale.toSource(constraint.timePeriod.to),
                            cscale.toSource(0),
                            isProposal,
                        ),
                        getRegulationLine(
                            `occ-peak-sub-period-transition-end`,
                            tscale.toSource(constraint.timePeriod.to),
                            cscale.toSource(peakRate),
                            tscale.toSource(constraint.timePeriod.to),
                            cscale.toSource(0),
                            isProposal,
                        ),
                    );
                }
                previousRate = rate;
                previousPeakRate = peakRate;
            }
        }
        return graph;
    };
    renderRegulationPeriodsForOtherCounts = (regulationPeriod: RegulationPeriods) => {
        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();

        const graph = new Array<ReactNode>();
        const subPeriods = regulationPeriod.constraints
            .slice()
            .sort((constraint1, constraint2) =>
                TimePeriodUtils.COMPARE_FUNCTION(constraint1.timePeriod, constraint2.timePeriod),
            );

        for (let idx = 0; idx < subPeriods.length; idx++) {
            const subPeriod = subPeriods[idx];

            if (subPeriod.timePeriod?.from && subPeriod.timePeriod?.to) {
                const svgComponent = (
                    <rect
                        key={`sub-period-${idx}`}
                        x={tscale.toSource(subPeriod.timePeriod.from)}
                        y={cscale.toSource(0) - REGULATION_BAR_HEIGHT}
                        width={tscale.toSource(subPeriod.timePeriod.to) - tscale.toSource(subPeriod.timePeriod.from)}
                        height={REGULATION_BAR_HEIGHT}
                        fill="white"
                    />
                );
                graph.push(svgComponent);
            }
        }
        return graph;
    };

    renderHotspotPeriods = (periods: Array<TimePeriod>) => {
        if (!periods) return;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();
        const graph = new Array<ReactNode>();

        const y0 = cscale.source.to > cscale.source.from ? cscale.source.from : cscale.source.to;
        const y1 =
            cscale.source.to > cscale.source.from
                ? cscale.source.to - cscale.source.from
                : cscale.source.from - cscale.source.to;
        const padding = 5;
        const fillColour = '#F1F2F330';

        for (let idx = 0; idx < periods.length; idx++) {
            const period = periods[idx];

            if (period?.from && period?.to) {
                graph.push([
                    <polyline
                        key={`hotspot-period-left-${idx}`}
                        stroke="white"
                        strokeWidth={2}
                        fill="none"
                        points={`${tscale.toSource(period.from) + padding},${y0} 
                              ${tscale.toSource(period.from)},${y0} 
                              ${tscale.toSource(period.from)},${y0 + y1} 
                              ${tscale.toSource(period.from) + padding},${y0 + y1}`}
                    />,
                    <rect
                        key={`hotspot-period-${idx}`}
                        fill={fillColour}
                        x={tscale.toSource(period.from)}
                        y={y0}
                        width={tscale.toSource(period.to) - tscale.toSource(period.from)}
                        height={y1}
                        shapeRendering="geometricPrecision"
                    />,
                    <polyline
                        key={`hotspot-period-right-${idx}`}
                        stroke="white"
                        strokeWidth={2}
                        fill="none"
                        points={`${tscale.toSource(period.to) - padding},${y0} 
                          ${tscale.toSource(period.to)},${y0} 
                          ${tscale.toSource(period.to)},${y0 + y1} 
                          ${tscale.toSource(period.to) - padding},${y0 + y1}`}
                    />,
                ]);
            }
        }
        return graph;
    };

    getNumberOfSelectedFlightsInBar = countingPeriod => {
        if (this.props.counts && this.props.highlightedFlightsOccupancy) {
            if (this.props.highlightedFlightsType === CountType.ENTRY) {
                return Array.from(this.props.highlightedFlightsOccupancy.values()).filter(tp =>
                    TimePeriodUtils.contains(countingPeriod, tp.from),
                ).length;
            } else {
                return Array.from(this.props.highlightedFlightsOccupancy.values()).filter(tp =>
                    TimePeriodUtils.intersects(countingPeriod, tp),
                ).length;
            }
        }
        return 0;
    };
    renderSelectionOccupancy = () => {
        if (!this.props.counts) return undefined;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();

        return this.props.counts.counts.map((slice, index) => {
            const tp = TimeRangeUtils.getTimePeriodAtStep(this.props.timeRange, index);
            const countingPeriod = TimeRangeUtils.getCountingPeriodAtStep(this.props.timeRange, index);
            const nbSelectedFlightsInBar = this.getNumberOfSelectedFlightsInBar(countingPeriod);

            if (nbSelectedFlightsInBar > 0)
                return (
                    <line
                        key={'selection-occupancy-' + index}
                        x1={tscale.toSource(tp.from)}
                        y1={cscale.toSource(nbSelectedFlightsInBar)}
                        x2={tscale.toSource(tp.to)}
                        y2={cscale.toSource(nbSelectedFlightsInBar)}
                        stroke="#04C0FF"
                        strokeWidth={'1px'}
                        shapeRendering="geometricPrecision"
                    />
                );
        });
    };
    renderCurrentTimeLine = () => {
        if (!TimePeriodUtils.contains(this.getDisplayPeriod(), this.props.currentTime)) return;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();

        return (
            <line
                x1={tscale.toSource(this.props.currentTime)}
                y1={cscale.source.from}
                x2={tscale.toSource(this.props.currentTime)}
                y2={cscale.source.to}
                stroke="#FFFFFF80"
                strokeWidth="1px"
                strokeDasharray="0.4em 0.4em"
                shapeRendering="crispEdges"
            />
        );
    };
    getCountTargetRange = () => {
        const maxCountValue =
            this.props.counts?.counts && Math.max(...this.props.counts.counts.map(slice => slice.total));
        const capacityValue =
            this.props.hourlyEntryCapacity &&
            Math.max(...this.props.hourlyEntryCapacity.map(capacityValue => capacityValue.hourlyCapacity));
        const peakValue = this.props.otmvDefns && Math.max(...this.props.otmvDefns.map(otmv => otmv.peakValue));
        const maxRegulationRate =
            this.props.regulationPeriods &&
            Math.max(
                ...this.props.regulationPeriods.map(periods => Math.max(...periods.constraints?.map(cst => cst.rate))),
            );

        if (maxCountValue || capacityValue || peakValue || maxRegulationRate) {
            const values = [maxCountValue, capacityValue, peakValue, maxRegulationRate].filter(value => !isNaN(value));
            return new Range(0, Math.max(...values) + COUNTS_RANGE_MARGIN);
        } else {
            return new Range(0, 50);
        }
    };
    renderTimeOver = () => {
        if (
            !this.getTimeOver() ||
            !this.props.timeRange ||
            !TimePeriodUtils.contains(this.props.timeRange, this.getTimeOver())
        )
            return null;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();
        const timeOverStep = TimeRangeUtils.getStep(this.props.timeRange, this.getTimeOver());
        const timePeriod = TimePeriodUtils.intersection(
            this.getDisplayPeriod(),
            TimeRangeUtils.getTimePeriodAtStep(this.props.timeRange, timeOverStep),
        );

        const barWidth = tscale.toSource(this.props.timeRange.to) - tscale.toSource(this.props.timeRange.from);
        const height = cscale.source.from - cscale.source.to;
        const polygonWidth = 5; // 20px for large bars, 50% of the bar width otherwise
        const posX = (tscale.toSource(timePeriod.from) + tscale.toSource(timePeriod.to)) / 2;

        const timeSlice = this.props.counts?.counts[timeOverStep];
        const timeSliceCapacity = this.props.hourlyEntryCapacity?.find(value =>
            TimePeriodUtils.contains(value.timePeriod, this.getTimeOver()),
        );
        const timeSlicePredictedCapacity = this.props.predictedCapacity?.find(value =>
            TimePeriodUtils.contains(value.timePeriod, this.getTimeOver()),
        );
        const timeSliceOtmv = this.props.otmvDefns?.find(value =>
            TimePeriodUtils.contains(value.timePeriod, this.getTimeOver()),
        );
        const timeSliceSubTotals: TimeSliceStandardSubCounts =
            timeSlice?.subtotals && TrafficCountUtils.isStandardSubCounts(timeSlice?.subtotals) && timeSlice?.subtotals;

        const countingPeriod = TimeRangeUtils.getCountingPeriodAtStep(this.props.timeRange, timeOverStep);
        const nbSelectedFlightsInBar = this.getNumberOfSelectedFlightsInBar(countingPeriod);
        const regOutlookPrediction = this.props.regOutlook?.[this.getTimeOver().getUTCHours()];

        return (
            <g>
                {this.props.timeRange.stepSizeInMinutes > 1 ? (
                    <>
                        <line
                            x1={tscale.toSource(timePeriod.to)}
                            y1={cscale.source.from}
                            x2={tscale.toSource(timePeriod.to)}
                            y2={cscale.source.to}
                            stroke="#FFFFFF"
                            strokeWidth="1px"
                            strokeDasharray="0.4em 0.4em"
                            shapeRendering="crispEdges"
                        />
                        <line
                            x1={tscale.toSource(timePeriod.from)}
                            y1={cscale.source.from}
                            x2={tscale.toSource(timePeriod.from)}
                            y2={cscale.source.to}
                            stroke="#FFFFFF"
                            strokeWidth="1px"
                            strokeDasharray="0.4em 0.4em"
                            shapeRendering="crispEdges"
                        />
                    </>
                ) : (
                    <line
                        x1={posX}
                        y1={cscale.source.from}
                        x2={posX}
                        y2={cscale.source.to}
                        stroke="#FFFFFF"
                        strokeWidth="1px"
                        strokeDasharray="0.4em 0.4em"
                        shapeRendering="crispEdges"
                    />
                )}
                <polygon
                    points={`${posX - polygonWidth / 2},${cscale.source.to} ${posX + polygonWidth / 2},${cscale.source.to} ${posX},${cscale.source.to + 0.866 * polygonWidth}`}
                    fill="#FFFFFF"
                />
                <polygon
                    points={`${posX - polygonWidth / 2},${cscale.source.from} ${posX + polygonWidth / 2},${cscale.source.from} ${posX},${cscale.source.from - 0.866 * polygonWidth}`}
                    fill="#FFFFFF"
                />
                {/* Setting timeOver as a key to force React to unmount/remount the foreignObject to update its position,
                    updating the x position is not sufficient and doesn't rerender it
                */}
                <foreignObject
                    x={tscale.toSource(timePeriod.from) - 100}
                    y={cscale.source.to}
                    width={120}
                    height={height}
                    key={this.getTimeOver()}>
                    <div className="timeover-panel-wrapper">
                        <div className="timeover-panel">
                            <div>
                                {this.props.timeRange.stepSizeInMinutes > 1
                                    ? TimeUtils.formatHHMM(countingPeriod.from) +
                                      '-' +
                                      TimeUtils.formatHHMM(countingPeriod.to)
                                    : TimeUtils.formatHHMM(this.getTimeOver())}
                            </div>
                            <div className="timeover-panel-content">
                                <div style={{ gridArea: '1 / 1' }}>
                                    <div>
                                        {timeSliceCapacity && (
                                            <span className={`hourly-capacity ${timeSliceCapacity.dataSource}`}>
                                                {timeSliceCapacity?.hourlyCapacity}
                                            </span>
                                        )}
                                        {timeSlicePredictedCapacity && (
                                            <span>
                                                {' '}
                                                -{' '}
                                                <span className={`hourly-capacity predicted`}>
                                                    {timeSlicePredictedCapacity.hourlyCapacity}
                                                </span>
                                            </span>
                                        )}
                                        {regOutlookPrediction && (
                                            <span
                                                className={`regOutlookPrediction`}>{`${(regOutlookPrediction * 100).toFixed(0)}%`}</span>
                                        )}
                                    </div>
                                    {timeSliceOtmv && (
                                        <>
                                            <div style={{ fontWeight: 'bold', color: 'red' }}>
                                                {timeSliceOtmv?.peakValue || null}
                                            </div>
                                            <div style={{ fontWeight: 'bold', color: 'orange' }}>
                                                {timeSliceOtmv?.sustainedValue || null}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div style={{ gridArea: '2 / 1', fontWeight: 'bold', color: '#8C8B82' }}>
                                    {timeSlice?.total}
                                </div>
                                {timeSliceSubTotals &&
                                    STANDARD_SUB_COUNTS.filter(key => timeSliceSubTotals[key])
                                        .reverse()
                                        .map((key, idx) => {
                                            return (
                                                <div
                                                    key={`subtotal-${key}`}
                                                    className="subTotal"
                                                    style={{ gridArea: `${idx + 2} / 2` }}>
                                                    <span className={`square ${key}`} />
                                                    {timeSliceSubTotals[key]}
                                                </div>
                                            );
                                        })}
                            </div>
                            {nbSelectedFlightsInBar > 0 ? (
                                <div style={{ fontWeight: 'bold', color: '#04C0FF' }}>{nbSelectedFlightsInBar}</div>
                            ) : undefined}
                        </div>
                    </div>
                </foreignObject>
            </g>
        );
    };
    /**
     * Transparent SVG elements that are used to detect when mouse is over a time slide, or leaves the graph zone.
     */
    renderMouseOverElements = () => {
        if (!this.props.timeRange) return null;

        const tscale = this.getTimeScale();
        const cscale = this.getCountScale();
        return (
            <g>
                <rect
                    key={`time-slice-all`}
                    className={`time-slice-all`}
                    x={tscale.getScale().source.from}
                    y={cscale.toSource(cscale.target.to)}
                    width={tscale.getScale().source.to - tscale.getScale().source.from}
                    height={cscale.toSource(cscale.target.from) - cscale.toSource(cscale.target.to)}
                    fill="transparent"
                    onMouseOut={this.handleMouseOut}
                    onMouseMove={this.handleMouseOver}
                    onMouseDown={this.handleMouseDown}
                    onMouseUp={this.handleMouseUp}
                />
            </g>
        );
    };
    renderSelectedPeriod = () => {
        // When the mouse is down, display the period being selected
        // Otherwise, display the selectedPeriod provided to the component
        let selectedPeriod;
        if (this.mouseDown) {
            selectedPeriod = this.selectedPeriod;
        } else if (this.props.selectedPeriod) {
            if (!TimePeriodUtils.intersects(this.getDisplayPeriod(), this.props.selectedPeriod)) return;
            selectedPeriod = TimePeriodUtils.intersection(this.getDisplayPeriod(), this.props.selectedPeriod);
        }

        if (selectedPeriod) {
            const tscale = this.getTimeScale();
            const cscale = this.getCountScale();

            if (!selectedPeriod?.to) {
                // Use timeOver value instead
                selectedPeriod = {
                    from: TimeUtils.min(selectedPeriod.from, this.state.timeOver),
                    to: TimeUtils.max(selectedPeriod.from, this.state.timeOver),
                };
            }

            return (
                <rect
                    key="selected-period"
                    fill="#FF000030"
                    x={tscale.toSource(selectedPeriod.from)}
                    y={cscale.source.to > cscale.source.from ? cscale.source.from : cscale.source.to}
                    width={tscale.toSource(selectedPeriod.to) - tscale.toSource(selectedPeriod.from)}
                    height={
                        cscale.source.to > cscale.source.from
                            ? cscale.source.to - cscale.source.from
                            : cscale.source.from - cscale.source.to
                    }
                    shapeRendering="geometricPrecision"
                />
            );
        }
    };
    setTimeOver = (timeOver: Date) => {
        this.setState({ timeOver: timeOver }, () => this.props.onTimeOverChange?.(timeOver));
    };
    getMouseEventTime = e => {
        const svgPt = SvgUtils.getSvgCoordinates(this.svgRef?.current, e);
        if (this.getTimeScale().getScale().source.contains(svgPt.x)) {
            return this.getTimeScale().toTarget(svgPt.x);
        }

        // Mouse outside of the graph - return closest valid value
        if (this.getTimeScale().getScale().source.from - svgPt.x < this.getTimeScale().getScale().source.to - svgPt.x) {
            return this.getTimeScale().toTarget(this.getTimeScale().getScale().source.from);
        } else {
            return this.getTimeScale().toTarget(this.getTimeScale().getScale().source.to);
        }
    };
    handleMouseOver = e => {
        this.setTimeOver(this.getMouseEventTime(e));
        this.mouseDown && this.updateSelectedTime(e);
    };
    handleMouseOut = () => {
        this.setTimeOver(null);
    };
    updateSelectedTime = e => {
        // Selected period end at the end of the targeted count bar
        const tp = TimeRangeUtils.getTimePeriodAtStep(
            this.props.timeRange,
            TimeRangeUtils.getStep(this.props.timeRange, this.getMouseEventTime(e)),
        );
        this.selectedPeriod = {
            from: TimeUtils.min(this.selectedPeriod.from, tp.from),
            to: TimeUtils.max(this.selectedPeriod.from, tp.to),
        };
    };
    handleMouseDown = e => {
        if (e.button !== 0) return; // Only consider left click

        if (!TimePeriodUtils.isValid(this.props.timeRange)) return;

        // Selected period starts at the beginning of the targeted count bar
        const tp = TimeRangeUtils.getTimePeriodAtStep(
            this.props.timeRange,
            TimeRangeUtils.getStep(this.props.timeRange, this.getMouseEventTime(e)),
        );
        this.mouseDown = true;
        this.selectedPeriod = {
            from: tp.from,
            to: null,
        };
    };
    handleMouseUp = (e: MouseEvent) => {
        if (e.button !== 0) return; // Only consider left click

        this.mouseDown = false;

        // if (!this.selectedPeriod?.from) return undefined

        this.updateSelectedTime(e);
        this.props.onSelectedPeriodChange?.(this.selectedPeriod, Utils.bitMask(e.shiftKey, e.ctrlKey, e.altKey));
    };
    getCountAxisWidth = () => {
        return (!this.props.hideCountAxis && VERTICAL_AXIS_WIDTH) || 0;
    };
    getTimeAxisHeight = () => {
        return (!this.props.hideTimeAxis && HORZ_AXIS_HEIGHT) || 0;
    };
    renderCountAxis = (height: number) => {
        if (this.props.hideCountAxis) return null;

        return (
            <CountAxis
                countScale={this.getCountScale()}
                orientation={AxisOrientation.LEFT}
                title="number of flights"
                height={height - this.getTimeAxisHeight() - MARGIN_TOP}
                width={this.getCountAxisWidth()}
                tickInterval={this.state.yAxisTickInterval}
                x={0}
                y={MARGIN_TOP}
            />
        );
    };
    renderTimeAxis = (height: number, width: number) => {
        if (this.props.hideTimeAxis) return null;

        return (
            <TimeAxis
                orientation={AxisOrientation.BOTTOM}
                labelFormat={TimeLabelFormat.HH_MM}
                timeScale={this.getTimeScale()}
                width={width - this.getCountAxisWidth()}
                tickInterval={this.state.xAxisTickInterval}
                height={this.getTimeAxisHeight()}
                x={this.getCountAxisWidth()}
                y={height - this.getTimeAxisHeight()}
            />
        );
    };

    renderSecondaryAxis = (height: number, width: number) => {
        if (Utils.isEmpty(this.props.regOutlook)) return null;

        return (
            <CountAxis
                countScale={this.regOutlookScale}
                orientation={AxisOrientation.RIGHT}
                title="reg outlook pred %"
                height={height - this.getTimeAxisHeight() - MARGIN_TOP}
                width={this.getCountAxisWidth()}
                tickInterval={20}
                x={width}
                y={MARGIN_TOP}
            />
        );
    };

    renderRegulationOutlook = () => {
        if (Utils.isEmpty(this.props.regOutlook)) return null;

        const points = this.props.regOutlook
            .map((pred, index) => {
                // Every 30 minutes
                const from = TimeUtils.addMinutes(AppContext.getTodayPeriod().from, 30 + index * 60);
                const value = pred * 100;
                return `${this.getTimeScale().toSource(from)},${this.regOutlookScale.toSource(value)}`;
            })
            .join(' ');

        return <polyline fill={'none'} stroke={'tan'} strokeWidth={'1px'} points={points} />;
    };

    render() {
        if (!this.props.timeRange) return null;
        return (
            <AutoSizer>
                {({ height, width }) => {
                    this.getTimeScale().setSource(new Range(this.getCountAxisWidth(), width));
                    this.getCountScale().source = new Range(height - this.getTimeAxisHeight(), MARGIN_TOP);
                    this.regOutlookScale.source = new Range(height - this.getTimeAxisHeight(), MARGIN_TOP);
                    const clip1Id = GraphUtils.getUniqueClipId();
                    return (
                        <div className="counts-graph" style={{ height: height, width: width }}>
                            <svg ref={this.svgRef} width="100%" height="100%" style={{ overflow: 'visible' }}>
                                <defs>
                                    <clipPath id={clip1Id}>
                                        <rect
                                            x={this.getCountAxisWidth()}
                                            y={0}
                                            width={width - this.getCountAxisWidth()}
                                            height={height - this.getTimeAxisHeight()}
                                        />
                                    </clipPath>
                                </defs>
                                {this.renderCountAxis(height)}
                                {this.renderTimeAxis(height, width)}
                                {this.renderSecondaryAxis(height, width)}
                                <g clipPath={`url(#${clip1Id})`}>
                                    {this.renderBackground()}
                                    {this.renderInactivePeriods()}
                                    {this.renderCounts()}
                                    {this.renderHorizontalGrid(this.state.yAxisTickInterval)}
                                    {this.renderRegulationOutlook()}
                                    {this.renderCapacity(this.props.hourlyEntryCapacity, 'orange')}
                                    {this.renderOtmv()}
                                    {this.renderComparisonCounts()}
                                    {this.renderRegulationPeriods(this.props.regulationPeriods, false)}
                                    {this.renderRegulationPeriods(this.props.proposalRegulationPeriods, true)}
                                    {this.renderHotspotPeriods(this.props.hotspotsPeriods)}
                                    {this.renderCapacity(this.props.predictedCapacity, 'violet')}
                                    {this.renderSelectionOccupancy()}
                                    {this.renderCurrentTimeLine()}
                                </g>
                                {this.renderSelectedPeriod()}
                                {this.renderTimeOver()}
                                {this.renderMouseOverElements()}
                            </svg>
                        </div>
                    );
                }}
            </AutoSizer>
        );
    }
}
